import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { onSnapshot, collection, query, where, getDocs, doc, setDoc, updateDoc, arrayUnion, arrayRemove, serverTimestamp, deleteDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../../config/firebaseConfig';
import webrtcService from './webrtcService';
import VideoCall from './VideoCall';
import JitsiMeeting from './JitsiMeeting';
import IncomingCallModal from './IncomingCallModal';
import { toast } from 'react-hot-toast';

const VideoCallContext = createContext();

export const useVideoCall = () => {
  const context = useContext(VideoCallContext);
  if (!context) {
    throw new Error('useVideoCall must be used within VideoCallProvider');
  }
  return context;
};

export const VideoCallProvider = ({ children }) => {
  const [incomingCall, setIncomingCall] = useState(null);
  const [activeCall, setActiveCall] = useState(null);
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [callStatus, setCallStatus] = useState('idle');
  const [conferenceParticipants, setConferenceParticipants] = useState([]);
  const [isHost, setIsHost] = useState(false);
  const [isJitsiConference, setIsJitsiConference] = useState(false);
  const [jitsiRoomName, setJitsiRoomName] = useState(null);
  const [currentUserName, setCurrentUserName] = useState('');
  const [currentUserEmail, setCurrentUserEmail] = useState('');

  // Leave call (for user leaving without ending for everyone)
  const handleLeaveCall = useCallback(async (roomId = null) => {
    try {
      const user = auth.currentUser;
      const conferenceRoomId = roomId || activeCall?.roomId;
      
      // If this is a conference, remove user from participants list
      if (conferenceRoomId?.startsWith('conference_') && user) {
        try {
          // Get user data for the leave notification
          const usersRef = collection(db, 'Users');
          const userQuery = query(usersRef, where('eMail', '==', user.email));
          const userSnapshot = await getDocs(userQuery);
          const userData = userSnapshot.docs[0]?.data();
          const userName = userData ? `${userData.eName} ${userData.eSurname}` : user.email;

          // Remove user from conference participants
          const conferenceRef = doc(db, 'conferences', conferenceRoomId);
          const conferenceSnap = await getDoc(conferenceRef);
          
          if (conferenceSnap.exists()) {
            const currentParticipants = conferenceSnap.data().participants || [];
            // Filter out the leaving user (handle both email string and object format)
            const updatedParticipants = currentParticipants.filter(p => {
              if (typeof p === 'string') return p !== user.email;
              return p.email !== user.email;
            });
            
            await updateDoc(conferenceRef, {
              participants: updatedParticipants,
              lastLeft: { email: user.email, name: userName, leftAt: serverTimestamp() }
            });
          }

          // Also remove from callRooms
          const callRoomRef = doc(db, 'callRooms', conferenceRoomId);
          await updateDoc(callRoomRef, {
            participants: arrayRemove(user.email)
          }).catch(() => {});
        } catch (err) {
          console.error('Error removing from conference:', err);
        }
      }

      // Close peer connection without updating call status
      if (webrtcService.peerConnection) {
        webrtcService.peerConnection.close();
        webrtcService.peerConnection = null;
      }

      // Stop local stream
      if (localStream) {
        localStream.getTracks().forEach(track => track.stop());
      }
      if (webrtcService.localStream) {
        webrtcService.localStream.getTracks().forEach(track => track.stop());
        webrtcService.localStream = null;
      }

      // Unsubscribe from listeners
      if (webrtcService.unsubscribe) {
        webrtcService.unsubscribe();
        webrtcService.unsubscribe = null;
      }

      // Clear callback
      webrtcService.onRemoteTrack = null;
      webrtcService.callId = null;
      webrtcService.remoteStream = null;

      setActiveCall(null);
      setLocalStream(null);
      setRemoteStream(null);
      setIsMuted(false);
      setIsVideoOff(false);
      setCallStatus('idle');
      setConferenceParticipants([]);
      setIsJitsiConference(false);
      setJitsiRoomName(null);

      toast('Aramadan ayrıldınız', { icon: 'ℹ️' });
    } catch (error) {
      console.error('Error leaving call:', error);
    }
  }, [localStream, activeCall?.roomId]);

  // End active call (ends for everyone - used for 1-on-1 calls)
  const handleEndCall = useCallback(async () => {
    try {
      // Check if this is a conference call - if so, just leave instead of ending
      if (activeCall?.roomId?.startsWith('conference_')) {
        await handleLeaveCall();
        return;
      }

      await webrtcService.endCall();

      if (localStream) {
        localStream.getTracks().forEach(track => track.stop());
      }

      // Mark call invitation messages as ended
      if (activeCall?.roomId) {
        try {
          const messagesRef = collection(db, 'messages');
          const q = query(messagesRef, where('callRoomId', '==', activeCall.roomId));
          const snapshot = await getDocs(q);

          snapshot.docs.forEach(async (docSnapshot) => {
            await updateDoc(docSnapshot.ref, {
              callEnded: true,
              endedAt: serverTimestamp()
            });
          });
        } catch (error) {
          console.error('Error updating call invitation messages:', error);
        }
      }

      setActiveCall(null);
      setLocalStream(null);
      setRemoteStream(null);
      setIsMuted(false);
      setIsVideoOff(false);
      setCallStatus('idle');

      toast('Call ended', { icon: 'ℹ️' });
    } catch (error) {
      console.error('Error ending call:', error);
    }
  }, [localStream, activeCall, handleLeaveCall]);

  // Listen for incoming calls
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    const callsRef = collection(db, 'calls');
    const q = query(
      callsRef,
      where('receiverId', '==', user.email),
      where('status', '==', 'ringing')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === 'added') {
          const callData = { id: change.doc.id, ...change.doc.data() };
          setIncomingCall(callData);
          setCallStatus('ringing');
        }
      });
    });

    return () => unsubscribe();
  }, []);

  // Monitor active call status
  useEffect(() => {
    if (!activeCall?.id) return;

    // Skip monitoring for conference calls - they don't auto-end when others leave
    if (activeCall?.roomId?.startsWith('conference_')) {
      return;
    }

    // Listen to the specific call document instead of the entire collection
    const callRef = doc(db, 'calls', activeCall.id);
    const unsubscribe = onSnapshot(callRef, (snapshot) => {
      if (!snapshot.exists()) {
        // Call document was deleted
        handleEndCall();
        return;
      }

      const data = snapshot.data();

      if (data.status === 'ended' || data.status === 'rejected') {
        handleEndCall();
      } else if (data.status === 'connected') {
        setCallStatus('connected');
      }
    });

    return () => unsubscribe();
  }, [activeCall?.id, activeCall?.roomId, handleEndCall]);

  // Track previous participants for detecting leaves
  const prevParticipantsRef = React.useRef([]);

  // Monitor conference status - end call if conference is ended by host
  useEffect(() => {
    if (!activeCall?.roomId?.startsWith('conference_')) return;

    const conferenceRef = doc(db, 'conferences', activeCall.roomId);
    const unsubscribe = onSnapshot(conferenceRef, (snapshot) => {
      if (!snapshot.exists()) {
        // Conference was ended by host - force end for all participants
        toast('Toplantı sunucu tarafından sonlandırıldı', { icon: 'ℹ️' });
        handleLeaveCall();
        return;
      }

      const data = snapshot.data();
      const newParticipants = data.participants || [];
      
      // Check if someone left (show toast)
      if (data.lastLeft && data.lastLeft.email !== auth.currentUser?.email) {
        // Check if this is a new leave event (not already shown)
        const lastLeftEmail = data.lastLeft.email;
        const wasInPrev = prevParticipantsRef.current.some(p => 
          (typeof p === 'string' ? p : p.email) === lastLeftEmail
        );
        const notInNew = !newParticipants.some(p => 
          (typeof p === 'string' ? p : p.email) === lastLeftEmail
        );
        
        if (wasInPrev && notInNew) {
          const leaverName = data.lastLeft.name || data.lastLeft.email.split('@')[0];
          toast(`${leaverName} ayrıldı`, { icon: '👋' });
        }
      }
      
      // Update previous participants ref
      prevParticipantsRef.current = newParticipants;
      
      // Update participants list
      setConferenceParticipants(newParticipants);
      // Check if current user is the host
      setIsHost(data.hostEmail === auth.currentUser?.email);
    });

    return () => unsubscribe();
  }, [activeCall?.roomId, handleLeaveCall]);

  // End conference for everyone (host only)
  const handleEndConference = useCallback(async () => {
    if (!activeCall?.roomId?.startsWith('conference_')) return;
    
    const user = auth.currentUser;
    if (!user) return;

    try {
      // Delete the conference document - this will trigger all participants to leave
      const conferenceRef = doc(db, 'conferences', activeCall.roomId);
      await deleteDoc(conferenceRef);

      // Also delete the callRooms document
      const callRoomRef = doc(db, 'callRooms', activeCall.roomId);
      await deleteDoc(callRoomRef);

      // Clean up local state
      await handleLeaveCall();
      
      toast.success('Conference ended');
    } catch (error) {
      console.error('Error ending conference:', error);
      toast.error('Failed to end conference');
    }
  }, [activeCall?.roomId, handleLeaveCall]);

  // Get user data helper
  const getUserData = async (email) => {
    try {
      const usersRef = collection(db, 'Users');
      const q = query(usersRef, where('eMail', '==', email));
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        return snapshot.docs[0].data();
      }
      return null;
    } catch (error) {
      console.error('Error getting user data:', error);
      return null;
    }
  };

  // Accept incoming call
  const handleAcceptCall = async () => {
    try {
      setCallStatus('connecting');

      const stream = await webrtcService.getUserMedia(true, true);
      setLocalStream(stream);

      // Set up callback for remote tracks
      webrtcService.onRemoteTrack = (remoteStream) => {
        console.log('Remote stream updated with tracks:', remoteStream.getTracks().length);
        setRemoteStream(remoteStream);
      };

      await webrtcService.answerCall(incomingCall.id);

      setActiveCall(incomingCall);
      setIncomingCall(null);

      toast.success('Call connected!');
    } catch (error) {
      console.error('Error accepting call:', error);
      toast.error('Failed to accept call. Please check your camera and microphone permissions.');
      setCallStatus('idle');
    }
  };

  // Reject incoming call
  const handleRejectCall = async () => {
    try {
      if (incomingCall?.id) {
        await webrtcService.rejectCall(incomingCall.id);
      }
      setIncomingCall(null);
      setCallStatus('idle');
      toast('Call rejected', { icon: 'ℹ️' });
    } catch (error) {
      console.error('Error rejecting call:', error);
    }
  };

  // Toggle mute
  const handleToggleMute = () => {
    const muted = webrtcService.toggleMute();
    setIsMuted(muted);
  };

  // Toggle video
  const handleToggleVideo = () => {
    const videoOff = webrtcService.toggleVideo();
    setIsVideoOff(videoOff);
  };

  // Join a call room (defined before initiateVideoCall since it depends on this)
  const joinCallRoom = useCallback(async (callRoomId, callType = 'video') => {
    try {
      setCallStatus('connecting');
      // Reset video/audio state for new call
      setIsVideoOff(false);
      setIsMuted(false);

      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const userData = await getUserData(user.email);
      const userName = userData ? `${userData.eName} ${userData.eSurname}` : user.email;
      
      // Store user info for Jitsi
      setCurrentUserName(userName);
      setCurrentUserEmail(user.email);

      // Check if this is a conference call - use Jitsi for conferences
      if (callRoomId.startsWith('conference_')) {
        // Update call room to add this user as participant
        const callRoomRef = doc(db, 'callRooms', callRoomId);
        await setDoc(callRoomRef, {
          participants: arrayUnion(user.email),
          status: 'active'
        }, { merge: true });

        // Add participant with name to the conference document
        const conferenceRef = doc(db, 'conferences', callRoomId);
        const conferenceSnap = await getDoc(conferenceRef);
        
        if (conferenceSnap.exists()) {
          const conferenceData = conferenceSnap.data();
          const currentParticipants = conferenceData.participants || [];
          
          // Check if user is already in participants
          const alreadyJoined = currentParticipants.some(p => 
            (typeof p === 'string' ? p : p.email) === user.email
          );
          
          if (!alreadyJoined) {
            // Add participant object with name
            await updateDoc(conferenceRef, {
              participants: arrayUnion({ email: user.email, name: userName })
            });
          }

          // Check if current user is the host
          setIsHost(conferenceData.hostEmail === user.email);
        }

        // Use Jitsi for conference calls
        setIsJitsiConference(true);
        setJitsiRoomName(callRoomId);
        setActiveCall({
          roomId: callRoomId,
          userName: userName,
          isInitiator: false,
          callType: 'video'
        });

        toast.success('Toplantıya katılındı!');
        setCallStatus('connected');
        return;
      }

      // For non-conference calls, use existing WebRTC logic
      // Get user media
      const videoEnabled = callType === 'video';
      const stream = await webrtcService.getUserMedia(videoEnabled, true);
      setLocalStream(stream);

      if (!videoEnabled) {
        setIsVideoOff(true);
      }

      // Set up callback for remote tracks
      webrtcService.onRemoteTrack = (remoteStream) => {
        console.log('Remote stream updated with tracks:', remoteStream.getTracks().length);
        setRemoteStream(remoteStream);
      };

      // Update call room to add this user as participant (use setDoc with merge to handle both new and existing docs)
      const callRoomRef = doc(db, 'callRooms', callRoomId);
      await setDoc(callRoomRef, {
        participants: arrayUnion(user.email),
        status: 'active'
      }, { merge: true });

      // Get call room data
      const callRoomSnap = await getDocs(query(collection(db, 'callRooms'), where('roomId', '==', callRoomId)));
      const callRoomData = callRoomSnap.docs[0]?.data();

      // Create or answer WebRTC connection
      const isInitiator = callRoomData?.createdBy === user.email;

      if (isInitiator) {
        // Creator: create offer
        const callId = await webrtcService.createCall(
          user.email,
          userName,
          callRoomId, // Use room ID as receiver
          'Call Room'
        );

        setActiveCall({
          id: callId,
          roomId: callRoomId,
          userName: userName,
          isInitiator: true,
          callType
        });
      } else {
        // Joiner: wait for offer and create answer
        setActiveCall({
          roomId: callRoomId,
          userName: userName,
          isInitiator: false,
          callType
        });

        // Look for existing call offer for this room (check for ringing OR connected status)
        const callsQuery = query(
          collection(db, 'calls'),
          where('receiverId', '==', callRoomId)
        );

        const callsSnapshot = await getDocs(callsQuery);
        if (!callsSnapshot.empty) {
          // Find the most recent call that has an offer
          const validCalls = callsSnapshot.docs.filter(doc => {
            const data = doc.data();
            return data.offer && (data.status === 'ringing' || data.status === 'connected');
          });
          
          if (validCalls.length > 0) {
            const callDoc = validCalls[0];
            await webrtcService.answerCall(callDoc.id);

            setActiveCall(prev => ({
              ...prev,
              id: callDoc.id
            }));
          }
        }
      }

      toast.success('Joined call!');
      setCallStatus('connected');
    } catch (error) {
      console.error('Error joining call room:', error);
      toast.error('Failed to join call. Please check your camera and microphone permissions.');
      setCallStatus('idle');

      if (localStream) {
        localStream.getTracks().forEach(track => track.stop());
        setLocalStream(null);
      }
    }
  }, [callStatus, localStream]);

  // Initiate a video call - sends a link to chat and auto-joins
  const initiateVideoCall = useCallback(async (receiverId, receiverName, receiverImage, sendMessageCallback) => {
    try {
      if (callStatus !== 'idle') {
        toast.error('You are already in a call');
        return;
      }

      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const userData = await getUserData(user.email);
      const callerName = userData ? `${userData.eName} ${userData.eSurname}` : user.email;

      // Create a unique call room ID
      const callRoomId = `call_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Create call room document in Firestore
      const callRoomRef = doc(db, 'callRooms', callRoomId);
      await setDoc(callRoomRef, {
        roomId: callRoomId,
        createdBy: user.email,
        createdByName: callerName,
        participants: [user.email],
        status: 'waiting',
        createdAt: serverTimestamp(),
        type: 'video'
      });

      // Send call invitation message through chat
      if (sendMessageCallback) {
        await sendMessageCallback({
          type: 'call_invitation',
          callType: 'video',
          callRoomId: callRoomId,
          callerName: callerName,
          text: `${callerName} is inviting you to a video call`
        });
      }

      toast.success('Call invitation sent!');

      // Automatically join the call room
      await joinCallRoom(callRoomId, 'video');
    } catch (error) {
      console.error('Error initiating call:', error);
      toast.error('Failed to send call invitation.');
    }
  }, [callStatus, joinCallRoom]);

  // Initiate a voice call (audio only) - sends a link to chat
  const initiateVoiceCall = useCallback(async (receiverId, receiverName, receiverImage, sendMessageCallback) => {
    try {
      if (callStatus !== 'idle') {
        toast.error('You are already in a call');
        return;
      }

      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const userData = await getUserData(user.email);
      const callerName = userData ? `${userData.eName} ${userData.eSurname}` : user.email;

      // Create a unique call room ID
      const callRoomId = `call_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Create call room document in Firestore
      const callRoomRef = doc(db, 'callRooms', callRoomId);
      await setDoc(callRoomRef, {
        roomId: callRoomId,
        createdBy: user.email,
        createdByName: callerName,
        participants: [user.email],
        status: 'waiting',
        createdAt: serverTimestamp(),
        type: 'voice'
      });

      // Send call invitation message through chat
      if (sendMessageCallback) {
        await sendMessageCallback({
          type: 'call_invitation',
          callType: 'voice',
          callRoomId: callRoomId,
          callerName: callerName,
          text: `${callerName} is inviting you to a voice call`
        });
      }

      toast.success('Call invitation sent!');
    } catch (error) {
      console.error('Error initiating voice call:', error);
      toast.error('Failed to send call invitation.');
    }
  }, [callStatus]);

  const value = {
    initiateVideoCall,
    initiateVoiceCall,
    joinCallRoom,
    callStatus,
    activeCall
  };

  return (
    <VideoCallContext.Provider value={value}>
      {children}

      {/* Incoming Call Modal */}
      {incomingCall && callStatus === 'ringing' && (
        <IncomingCallModal
          show={true}
          callerName={incomingCall.callerName}
          callerImage={incomingCall.callerImage}
          callType="video"
          onAccept={handleAcceptCall}
          onReject={handleRejectCall}
        />
      )}

      {/* Jitsi Conference Call */}
      {isJitsiConference && jitsiRoomName && (callStatus === 'connecting' || callStatus === 'connected') && (
        <JitsiMeeting
          roomName={jitsiRoomName}
          displayName={currentUserName}
          email={currentUserEmail}
          isHost={isHost}
          onLeave={handleLeaveCall}
          onEndConference={handleEndConference}
          participants={conferenceParticipants}
        />
      )}

      {/* Regular Video Call (1-on-1) */}
      {activeCall && localStream && !isJitsiConference && (callStatus === 'connecting' || callStatus === 'connected') && (
        <VideoCall
          localStream={localStream}
          remoteStream={remoteStream}
          onEndCall={handleEndCall}
          onLeaveCall={handleLeaveCall}
          onEndConference={handleEndConference}
          isMuted={isMuted}
          isVideoOff={isVideoOff}
          onToggleMute={handleToggleMute}
          onToggleVideo={handleToggleVideo}
          callerName={
            activeCall.receiverId === auth.currentUser?.email
              ? activeCall.callerName
              : activeCall.receiverName
          }
          isInitiator={activeCall.callerId === auth.currentUser?.email}
          isConference={false}
          isHost={isHost}
          participants={conferenceParticipants}
          currentUserEmail={auth.currentUser?.email || ''}
        />
      )}
    </VideoCallContext.Provider>
  );
};
